import React, { HTMLAttributeAnchorTarget, useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import TrackingContext from 'contexts/trackingContext'

import BodyText from 'components/Luxkit/Typography/BodyText'
import BaseYouMayAlsoLikeTile from '../BaseYouMayAlsoLikeTile'
import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import offerPageURL from 'lib/offer/offerPageURL'
import HotelPriceContent from './HotelPriceContent'
import BedbankHotelPriceContent from './BedbankHotelPriceContent'
import BundledHotelPriceContent from './BundledHotelPriceContent'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { GAProductFieldObject } from 'analytics/eventDefinitions'
import { productClick } from 'analytics/enhanced-ecommerce'
import { TileViewProps, YmalPricingProperties } from '../Common/types'
import { breakpointSizes } from 'components/utils/breakpoint'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import { getLereListSource } from 'components/Recommendations/common/constant'
import { isBedbank } from 'lib/offer/offerTypes'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'

const PriceDetailsColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
`

const Labels = styled(LabelGroup)``

// Need to force display flex because <Carousel /> keeps overwriting it as display: block.
const BaseTile = styled(BaseYouMayAlsoLikeTile)`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  &.force-flex.force-flex {
    display: flex;
  }

  ${BaseYouMayAlsoLikeTile.ImageBadges} {
    left: ${rem(8)};
    right: auto;
  }

  ${BaseYouMayAlsoLikeTile.PriceDetails} {
    display: flex;
    width: 100%;
    flex-wrap: wrap;

    ${PriceDetailsColumn}.deposit {
      flex: 1;
      min-width: 100%;
      margin-bottom: ${rem(8)};
    }
    ${PriceDetailsColumn}.price-amounts {
      flex: 1;
    }
  }

  ${BaseYouMayAlsoLikeTile.MainDescription} {
    overflow-y: auto;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    line-clamp: 5;
    overflow: hidden;

    .tile-body {
      margin-top: ${rem(4)};
    }

    ${Labels} {
      margin: ${rem(8)} 0 0 -${rem(1)};
    }
  }
`

const UrgencyLabelsWrapper = styled.div`
   display: flex;
   flex-wrap: wrap;
`

const ClickableWrapper = styled(ClickableLink)`
  display: flex;
  justify-content: center;
  width: ${rem(348)};
  min-height: ${rem(416)};
  flex-direction: column;

  & > *:first-child {
    flex: 1;
  }
`

export interface Props extends TileViewProps {
  anchorTarget?: HTMLAttributeAnchorTarget;
  offer: App.Offer | App.OfferSummary | App.BedbankOffer;
  linkQuery?: string;
  position: number;
  flightOrigin?: string;
  testId?: string;
  currencyCode: string;
  productClickObjectMapper?: (offer: App.Offer | App.OfferSummary | App.BedbankOffer, pricing: YmalPricingProperties, position: number) => GAProductFieldObject;
  alternativeCheckIn?: string,
  alternativeCheckOut?: string,
  alternativePrice?: number,
  alternativeMemberPrice?: number,
}

function YmalOfferTileSmall({
  anchorTarget,
  offer,
  linkQuery,
  position,
  flightOrigin,
  testId,
  currencyCode,
  depositAmountPercentage,
  depositAmount,
  depositEnabled,
  depositThresholds,
  distance,
  distanceFrom,
  distanceUnit,
  duration,
  tourDepositType,
  pricing,
  productClickObjectMapper,
  alternativeCheckIn,
  alternativeCheckOut,
  alternativePrice,
  alternativeMemberPrice,
}: Props) {
  const isBedbankOffer = isBedbank(offer)
  const tracking = useContext(TrackingContext)
  const { image, property, walledGarden } = offer
  const title = property?.name
  const titleBody = isBedbankOffer ? offer.tagline : offer.name
  const location = isBedbankOffer ? (offer.property.locationTitleOverride ?? [offer.property?.address?.city, offer.property?.address?.countryName].filter(t => t).join(', ')) : [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const luxPlusEnabled = useAppSelector(state => isLuxPlusEnabled(state))

  const productClickObject = useMemo(() => {
    return productClickObjectMapper ? productClickObjectMapper(offer, pricing, position) : {
      id: offer.id,
      name: offer.name,
      category: offer.analyticsType,
      price: pricing?.price,
      memberPrice: pricing?.memberPrice,
      quantity: 1,
      position,
    }
  }, [productClickObjectMapper, offer, position, pricing])

  const fireProductClick = useCallback(() => {
    const lereVersion = getLereListSource(tracking?.lere?.version)
    productClick(
      currencyCode,
      productClickObject,
      { listId: tracking.listId, listName: tracking.listName, listSource: lereVersion },
    )
  }, [
    currencyCode,
    tracking?.listId,
    tracking?.listName,
    tracking?.lere?.version,
    productClickObject,
  ])

  const onClick = useCallback(() => {
    if (!tracking) { return }

    fireProductClick()
  }, [fireProductClick, tracking])

  const onClickSecureDeposit = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
  }, [])

  const clickToDestination = useMemo(() => [offerPageURL(offer), linkQuery ?? ''].join('?'), [offer, linkQuery])
  const isUltraLux = !isBedbankOffer && offer.property?.isUltraLux || offer.productType === 'ultra_lux_tour'
  const productType = isBedbankOffer ? (offer.promotions?.length ? 'limited_time_special' : 'bedbank_hotel') : offer.productType
  const paletteValue: ProductPalette = luxPlusEnabled && !!offer.luxPlus?.access ? 'loyalty' : isUltraLux ? 'ultralux' : 'default'

  return (
    <ClickableWrapper to={clickToDestination} target={anchorTarget} onClick={onClick}>
      <BaseTile
        className="force-flex"
        image={image}
        location={location}
        locationDistance={distance}
        locationDistanceFrom={distanceFrom}
        locationDistanceUnit={distanceUnit}
        title=""
        subtitle={null}
        walledGarden={walledGarden}
        variant="bordered"
        testId={testId}
        imageXSRatio="350:180"
        imageXSWidth={`${breakpointSizes.mobile.max}px`}
        imageSMRatio="350:180"
        imageSMWidth="350px"
      >
        <BaseTile.ImageBadges>
          <ProductTypeLabel productType={productType} />
        </BaseTile.ImageBadges>
        <BaseTile.ContentTags>
          {!isBedbankOffer && <UrgencyLabelsWrapper data-testid="urgency-labels">
            <OfferLabels offer={offer} />
          </UrgencyLabelsWrapper>}
        </BaseTile.ContentTags>
        <BaseTile.MainDescription>
          {title && <Heading variant="heading6">{title}</Heading>}
          {titleBody && <BodyText variant="medium" className="tile-body">
            {titleBody}
          </BodyText>}
        </BaseTile.MainDescription>
        <BaseTile.PriceDetails>
          {depositEnabled && <PriceDetailsColumn className="deposit">
            <SecureWithDepositTagAndModal
              testId="secure-deposit-tag"
              depositThresholds={depositThresholds}
              depositAmount={depositAmount}
              depositAmountPercentage={depositAmountPercentage}
              depositType={tourDepositType}
              offerType={offer.type}
              onClick={onClickSecureDeposit}
            />
          </PriceDetailsColumn>}
          <PriceDetailsColumn className="price-amounts">
            {isBedbankOffer && <BedbankHotelPriceContent duration={duration}
            offer={offer}
            alternativeCheckIn={alternativeCheckIn}
            alternativeCheckOut={alternativeCheckOut}
            alternativePrice={alternativePrice}
           />}
            {!isBedbankOffer && !offer.bundledWithFlightsOnly && <HotelPriceContent duration={duration}
            offer={offer}
            flightOrigin={flightOrigin}
            alternativeCheckIn={alternativeCheckIn}
            alternativeCheckOut={alternativeCheckOut}
            alternativePrice={alternativePrice}
            alternativeMemberPrice={alternativeMemberPrice}
           />}
            {!isBedbankOffer && offer.bundledWithFlightsOnly && <BundledHotelPriceContent duration={duration} offer={offer}/>}
          </PriceDetailsColumn>
          <PriceDetailsColumn>
            <ProductPaletteProvider palette={paletteValue}>
              <TextButton kind="primary">View Offer</TextButton>
            </ProductPaletteProvider>
          </PriceDetailsColumn>
        </BaseTile.PriceDetails>
      </BaseTile>
    </ClickableWrapper>
  )
}

export default YmalOfferTileSmall
